import { useState, useEffect, useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import AvatarIcon from "@/assets/svg/avatar.svg?react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, LogOut, Sun } from "lucide-react";
import { useAccount, useWorkspaces } from "@/api/auth/queries";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { paths } from "@/router/paths";
import useLogout from "@/hooks/useLogout";
import { useTheme } from "@/contexts/Theme";
import { getUserImg, getWorkspaceImg } from "@/helpers/workspaces";
import WorkspaceCard from "./WorkspaceCard";

type Props = {
  isExpanded: boolean;
  side?: "bottom" | "top" | "right" | "left";
  align?: "start" | "center" | "end";
};

const WorkspaceSwitcher = ({
  isExpanded,
  align = "center",
  side = "right",
}: Props) => {
  const [open, setOpen] = useState(false);
  const { workspace, setWorkspace } = useAuth();
  const { data: workspaces } = useWorkspaces();
  const { data: account } = useAccount();
  const navigate = useNavigate();
  const logOut = useLogout();
  const { theme, toggleTheme } = useTheme();

  const goToWorkspaceCreatePage = () =>
    navigate(paths.auth.workspaces + "?create");

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "1" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        goToWorkspaceCreatePage();
      }
      if (e.key === "2" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleTheme();
      }
      if (e.key === "3" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        logOut();
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [theme]);

  const activeWorkspace = useMemo(
    () =>
      workspaces && workspace
        ? workspaces.find((wu) => wu.workspace.id === +workspace)?.workspace
        : null,
    [workspace, workspaces]
  );

  if (!workspace || !workspaces || !workspaces.length || !account) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={`${isExpanded ? "rounded-xl p-2 justify-between w-full h-auto" : "rounded-full p-1 justify-center w-15 h-15"} truncate`}>
          <div className="flex gap-2 items-center relative">
            <AvatarIcon className="h-14 w-14" />
            {/* <div className="absolute h-14 w-14 text-lg font-light text-white flex justify-center items-center">
              {account.name
                .split(" ")
                .map((subname) => subname?.slice(0, 1))
                .join("")}
            </div> */}
            <img
              className="absolute h-10 w-10 top-2 left-2 rounded-full object-cover object-center"
              src={account ? getUserImg(account) : ""}
            />
            <div className="absolute bottom-1 text-xs text-white flex justify-center items-center left-8 w-5 h-5 bg-transparent rounded-lg shadow-xl backdrop-blur-xl">
              <img
                width={24}
                className="rounded-md object-cover object-center"
                src={activeWorkspace ? getWorkspaceImg(activeWorkspace) : ""}
              />
            </div>

            {isExpanded ? (
              <div className="flex text-left  max-w-[110px] flex-col">
                <div className="truncate">{account.name}</div>
                <div className="text-xs opacity-80 truncate">
                  {activeWorkspace?.name}
                </div>
              </div>
            ) : null}
          </div>

          {isExpanded ? (
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-[100vw] lg:w-72 border-none shadow-none bg-transparent">
        <Command className="rounded-xl border shadow-lg">
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup heading={`${account.name} - Workspaces`}>
              <div className="max-h-[120px] overflow-y-auto">
                {workspaces.map((wu) => (
                  <CommandItem
                    onSelect={() => setWorkspace(wu.workspace.id.toString())}
                    className="cursor-pointer flex justify-between"
                    key={wu.workspace.id}>
                    <WorkspaceCard workspace={wu.workspace} />
                    {workspace === wu.workspace.id.toString() && (
                      <Check className="ml-2 h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </div>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Quick Actions">
              <CommandItem
                onSelect={goToWorkspaceCreatePage}
                className="cursor-pointer">
                <Plus className="mr-2 h-4 w-4" />
                <span>Add Workspace</span>
                <CommandShortcut className="hidden lg:block">
                  ⌘1
                </CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={toggleTheme} className="cursor-pointer">
                <Sun className="mr-2 h-4 w-4" />
                <span>Toggle Theme</span>
                <CommandShortcut className="hidden lg:block">
                  ⌘2
                </CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={logOut} className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log Out</span>
                <CommandShortcut className="hidden lg:block">
                  ⌘3
                </CommandShortcut>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default WorkspaceSwitcher;
